import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import {
  Route as RouteIcon, Lock, ArrowLeft, AlertTriangle, CheckCircle2, Zap, Truck, CalendarX,
} from 'lucide-react';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, getTodayInTz, formatLocalDateInTz, formatDateTimeForInput } from '../../utils/timezone';
import { addDays } from '../../utils/payPeriod';
import { errorMessage } from '../../utils/apiResponse';
import { cn } from '../../utils/cn';
import { NewLoadDialog } from '../../components/nursery/despatch/NewLoadDialog';
import { LoadStatusBadge } from '../../components/nursery/despatch/LoadStatusBadge';
import { despatchWhen, addressLine } from '../../components/nursery/despatch/despatchText';
import type { LoadFieldsValue } from '../../components/nursery/despatch/LoadFields';
import {
  type DespatchOrder,
  type DespatchPlanner as Planner,
  type NurseryDeliveryZone,
} from '../../types/nursery';

/**
 * /nursery/despatch — every open order due in a window, grouped by zone then
 * customer, pulled onto loads from here.
 *
 * The banner is the point of the page: how many orders due in the window are
 * on no load. A bare-root nursery takes orders all year and despatches in one
 * month of winter; this is what stops that month closing with somebody
 * missed. Overdue and undated orders show in every window for the same reason.
 *
 * Plan: docs/nursery-despatch-plan.md, Phase 1.
 */

const PRESETS = [7, 30, 90];

interface Group {
  key: string;
  title: string;
  orders: DespatchOrder[];
}

function groupOrders(orders: DespatchOrder[], t: TFunction<'nursery', 'DespatchPlanner'>): Group[] {
  const map = new Map<string, Group>();
  for (const o of orders) {
    // A cross dock order goes to the dock, not to its store's zone: it is
    // planned by the route and cut-off the schedule gave it.
    const key = o.despatch_method === 'collection' ? 'collection'
      : o.despatch_method === 'cross_dock' ? `xdock:${o.xdock_route ?? ''}`
        : o.delivery_zone_id ? `zone:${o.delivery_zone_id}` : 'nozone';
    const title = key === 'collection' ? t('groupCollections')
      : o.despatch_method === 'cross_dock' ? t('groupCrossDock', { route: o.xdock_route || t('noRoute') })
        : o.delivery_zone_name || t('noZone');
    if (!map.has(key)) map.set(key, { key, title, orders: [] });
    map.get(key)!.orders.push(o);
  }
  const rank = (g: Group) => (g.key === 'collection' ? 0 : g.key === 'nozone' ? 3 : g.key.startsWith('xdock:') ? 1 : 2);
  const groups = [...map.values()].sort((a, b) => rank(a) - rank(b) || a.title.localeCompare(b.title));
  for (const g of groups) {
    g.orders.sort((a, b) =>
      g.key === 'collection'
        ? (a.collection_at ?? '9').localeCompare(b.collection_at ?? '9')
        : (a.customer_name ?? '').localeCompare(b.customer_name ?? '') || a.order_number.localeCompare(b.order_number),
    );
  }
  return groups;
}

function DespatchPlanner() {
  const { t } = useTranslation('nursery', { keyPrefix: 'DespatchPlanner' });
  const navigate = useNavigate();
  const tz = useOrgTimezone();
  const today = getTodayInTz(tz);
  const { hasAccess: canManage, loading: licenseLoading } = useModuleLicense('nursery', ['manager', 'admin']);

  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(addDays(today, 30));
  const [data, setData] = useState<Planner | null>(null);
  const [zones, setZones] = useState<NurseryDeliveryZone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showOnLoad, setShowOnLoad] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [targetLoadId, setTargetLoadId] = useState<number | ''>('');
  const [nowFarmId, setNowFarmId] = useState<number | ''>('');
  const [newLoad, setNewLoad] = useState<LoadFieldsValue | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!from || !to || to < from) return;
    setLoading(true);
    setError(null);
    try {
      const [res, zoneRes] = await Promise.all([
        nurseryService.getDespatchPlanner(from, to),
        nurseryService.getDeliveryZones(),
      ]);
      if (res.success) {
        setData(res.data);
        setSelected(new Set());
        setNowFarmId((f) => f || (res.data.farms.length === 1 ? res.data.farms[0].id : ''));
      } else {
        setError(errorMessage(res, t('loadFailed')));
      }
      if (zoneRes.success) setZones(zoneRes.data || []);
    } catch (e) {
      setError(errorMessage(e, t('loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [from, to, t]);

  useEffect(() => {
    if (canManage) load();
  }, [canManage, load]);

  const orders = useMemo(() => data?.orders ?? [], [data]);
  const byId = useMemo(() => new Map(orders.map((o) => [o.id, o])), [orders]);

  // Needs to go today: ASAP, or a collection booked for today or earlier.
  const despatchNow = useMemo(
    () => orders.filter((o) => !o.load_id && (
      o.priority === 'asap'
      || (o.despatch_method === 'collection' && o.collection_at && formatDateTimeForInput(o.collection_at, tz).slice(0, 10) <= today)
    )),
    [orders, tz, today],
  );
  const nowIds = useMemo(() => new Set(despatchNow.map((o) => o.id)), [despatchNow]);

  const visible = useMemo(
    () => orders.filter((o) => !nowIds.has(o.id) && (showOnLoad || !o.load_id)),
    [orders, nowIds, showOnLoad],
  );
  const groups = useMemo(() => groupOrders(visible, t), [visible, t]);

  const selectedOrders = [...selected].map((id) => byId.get(id)).filter((o): o is DespatchOrder => !!o);
  const selectedTrees = selectedOrders.reduce((n, o) => n + o.total_trees, 0);

  const toggle = (id: number) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleGroup = (g: Group) => setSelected((s) => {
    const ids = g.orders.filter((o) => !o.load_id).map((o) => o.id);
    const all = ids.every((id) => s.has(id));
    const next = new Set(s);
    for (const id of ids) if (all) next.delete(id); else next.add(id);
    return next;
  });

  const openNewLoad = () => {
    const methods = new Set(selectedOrders.map((o) => o.despatch_method));
    const only = methods.size === 1 ? [...methods][0] : null;
    const zoneIds = [...new Set(selectedOrders.map((o) => o.delivery_zone_id).filter((z): z is number => !!z))];
    setNewLoad({
      load_type: only === 'collection' ? 'collection' : only === 'post' ? 'post' : only === 'carrier' ? 'carrier'
        : only === 'cross_dock' ? 'cross_dock' : 'run',
      despatch_farm_id: data?.farms.length === 1 ? data.farms[0].id : 0,
      despatch_date: from > today ? from : today,
      zone_ids: zoneIds,
    });
  };

  const addToExisting = async () => {
    if (!targetLoadId || !selected.size) return;
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryService.addOrdersToLoad(targetLoadId, [...selected]);
      if (res.success) {
        setTargetLoadId('');
        await load();
      } else setError(errorMessage(res, t('addFailed')));
    } catch (e) {
      setError(errorMessage(e, t('addFailed')));
    } finally {
      setBusy(false);
    }
  };

  // Fill the selected orders from despatchable places, oldest ready first.
  const allocateSelected = async () => {
    if (!selected.size) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await nurseryService.autoAllocate([...selected]);
      if (!res.success) setError(errorMessage(res, t('allocateFailed')));
      else {
        const r = res.data;
        const short = r.orders.filter((o) => o.short.length > 0).length;
        setNotice(r.no_places
          ? t('noPlaces')
          : t('allocated', { count: r.trees, n: r.trees.toLocaleString() }) + (short ? t('stillShort', { count: short }) : ''));
        await load();
      }
    } catch (e) {
      setError(errorMessage(e, t('allocateFailed')));
    } finally {
      setBusy(false);
    }
  };

  const despatchToday = async () => {
    if (!nowFarmId || !despatchNow.length) return;
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryService.addToDailyLoads(despatchNow.map((o) => o.id), today, nowFarmId);
      if (res.success) await load();
      else setError(errorMessage(res, t('dailyFailed')));
    } catch (e) {
      setError(errorMessage(e, t('dailyFailed')));
    } finally {
      setBusy(false);
    }
  };

  if (licenseLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-40 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">{t('licenseTitle')}</h2>
          <p className="text-sm text-neutral-500 mb-4">
            <Trans t={t} i18nKey="licenseBody" components={{ b: <span className="font-medium" /> }} />
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate('/nursery')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> {t('backToNursery')}
          </Button>
        </div>
      </div>
    );
  }

  const s = data?.summary;
  const openLoads = data?.open_loads ?? [];

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        icon={<RouteIcon className="h-6 w-6 text-white" />}
        actionButtons={
          <Button variant="outline" size="sm" onClick={() => navigate('/nursery/loads')}>
            <Truck className="h-4 w-4" /> {t('loads')}
          </Button>
        }
      />
      <div className={cn('max-w-5xl mx-auto px-4 py-6 space-y-5', selected.size > 0 && 'pb-40')}>
        {/* Window */}
        <div className="bg-white border border-neutral-200 rounded-xl p-4 flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="plan-from" className="block text-xs font-medium text-neutral-500 mb-1">{t('from')}</label>
            <input id="plan-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className="border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label htmlFor="plan-to" className="block text-xs font-medium text-neutral-500 mb-1">{t('to')}</label>
            <input id="plan-to" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)}
              className="border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((d) => (
              <button key={d} type="button" onClick={() => { setFrom(today); setTo(addDays(today, d)); }}
                className="rounded-lg border border-neutral-300 px-3 py-2 text-sm text-neutral-600 hover:bg-neutral-50">
                {t('nextDays', { days: d })}
              </button>
            ))}
          </div>
        </div>

        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>}
        {notice && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-900 break-words">
            {notice} <Link to="/nursery/despatchable" className="underline">{t('despatchableLink')}</Link>
          </div>
        )}

        {loading && !data ? (
          <div className="h-48 bg-neutral-100 rounded-xl animate-pulse" />
        ) : s && (
          <>
            {/* The banner */}
            {s.not_on_load > 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 space-y-1">
                <p className="flex items-start gap-2 font-semibold">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600" />
                  <span>
                    {t('notOnLoad', { count: s.not_on_load, trees: s.not_on_load_trees.toLocaleString(), date: formatLocalDateInTz(to, tz) })}
                  </span>
                </p>
                {s.overdue_not_on_load > 0 && (
                  <p className="pl-7 text-red-700 font-medium">{t('overdue', { n: s.overdue_not_on_load })}</p>
                )}
                {s.undated > 0 && (
                  <p className="pl-7">{t('undated', { count: s.undated })}</p>
                )}
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-800 flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                <span>{t('allOnLoad', { date: formatLocalDateInTz(to, tz) })}</span>
              </div>
            )}
            {s.on_load_unallocated > 0 && (
              <p className="text-sm text-amber-800">
                {t('onLoadUnallocated', { count: s.on_load_unallocated })}
              </p>
            )}

            {/* Despatch now */}
            {despatchNow.length > 0 && (
              <section className="bg-white border border-orange-200 rounded-xl p-4" aria-labelledby="despatch-now">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <h2 id="despatch-now" className="flex items-center gap-2 text-sm font-semibold text-orange-800 uppercase tracking-wide">
                    <Zap className="h-4 w-4" /> {t('despatchNow', { n: despatchNow.length })}
                  </h2>
                  <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto min-w-0">
                    {(data?.farms.length ?? 0) > 1 && (
                      <select aria-label={t('despatchFrom')} value={nowFarmId} onChange={(e) => setNowFarmId(e.target.value ? Number(e.target.value) : '')}
                        className="border border-neutral-300 rounded-lg px-2 py-1.5 text-sm w-full sm:w-64 min-w-0">
                        <option value="">{t('whichFarm')}</option>
                        {data!.farms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                      </select>
                    )}
                    <Button size="sm" onClick={despatchToday} disabled={busy || !nowFarmId}>
                      {t('putOnToday')}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-neutral-500 mb-3">
                  {t('despatchNowNote')}
                </p>
                <ul className="divide-y divide-neutral-100">
                  {despatchNow.map((o) => <OrderRow key={o.id} order={o} tz={tz} />)}
                </ul>
              </section>
            )}

            <label className="flex items-center gap-2 text-sm text-neutral-600">
              <input type="checkbox" checked={showOnLoad} onChange={(e) => setShowOnLoad(e.target.checked)} />
              {t('showOnLoad', { n: s.on_load })}
            </label>

            {groups.length === 0 ? (
              <p className="text-sm text-neutral-500 text-center py-8">
                {orders.length ? t('everythingOnLoad') : t('noOpenOrders')}
              </p>
            ) : groups.map((g) => {
              const selectable = g.orders.filter((o) => !o.load_id);
              const allOn = selectable.length > 0 && selectable.every((o) => selected.has(o.id));
              const trees = g.orders.reduce((n, o) => n + o.total_trees, 0);
              return (
                <section key={g.key} className="bg-white border border-neutral-200 rounded-xl" aria-label={g.title}>
                  <header className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 border-b border-neutral-100">
                    <input
                      type="checkbox"
                      aria-label={t('selectAllIn', { group: g.title })}
                      checked={allOn}
                      disabled={!selectable.length}
                      onChange={() => toggleGroup(g)}
                    />
                    <h2 className="font-semibold text-neutral-900 break-words min-w-0 flex-1">{g.title}</h2>
                    <span className="text-xs text-neutral-500">
                      {t('groupSummary', { count: g.orders.length, trees: trees.toLocaleString() })}
                    </span>
                  </header>
                  <ul className="divide-y divide-neutral-100">
                    {g.orders.map((o) => (
                      <OrderRow key={o.id} order={o} tz={tz} selected={selected.has(o.id)} onToggle={o.load_id ? undefined : () => toggle(o.id)} />
                    ))}
                  </ul>
                </section>
              );
            })}
          </>
        )}
      </div>

      {/* Selection bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-neutral-200 shadow-lg pb-[var(--safe-bottom)]">
          <div className="max-w-5xl mx-auto px-4 py-3 flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-neutral-800">
              {t('selectedSummary', { n: selected.size, trees: selectedTrees.toLocaleString() })}
            </span>
            <Button size="sm" onClick={openNewLoad}>{t('newLoad')}</Button>
            <Button variant="outline" size="sm" onClick={allocateSelected} disabled={busy}>{t('allocate')}</Button>
            {openLoads.length > 0 && (
              <div className="flex items-center gap-2 min-w-0 w-full sm:w-auto">
                <select aria-label={t('addToExisting')} value={targetLoadId}
                  onChange={(e) => setTargetLoadId(e.target.value ? Number(e.target.value) : '')}
                  className="border border-neutral-300 rounded-lg px-2 py-1.5 text-sm flex-1 min-w-0 sm:w-72 sm:flex-none">
                  <option value="">{t('addToLoad')}</option>
                  {openLoads.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.load_number} · {t(`loadType.${l.load_type}`)} · {formatLocalDateInTz(l.despatch_date, tz)}
                    </option>
                  ))}
                </select>
                <Button variant="outline" size="sm" onClick={addToExisting} disabled={!targetLoadId || busy}>{t('add')}</Button>
              </div>
            )}
            <button type="button" className="text-sm text-neutral-500 underline ml-auto" onClick={() => setSelected(new Set())}>{t('clear')}</button>
          </div>
        </div>
      )}

      {newLoad && data && (
        <NewLoadDialog
          initial={newLoad}
          orderIds={[...selected]}
          trees={selectedTrees}
          farms={data.farms}
          zones={zones}
          onClose={() => setNewLoad(null)}
          onCreated={(l) => navigate(`/nursery/loads/${l.id}`)}
        />
      )}
    </>
  );
}

function OrderRow({ order: o, tz, selected, onToggle }: {
  order: DespatchOrder; tz: string; selected?: boolean; onToggle?: () => void;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'DespatchPlanner' });
  const when = despatchWhen(o, tz);
  const short = o.allocated_trees < o.total_trees;
  return (
    <li className="flex items-start gap-3 px-4 py-3">
      {onToggle !== undefined || o.load_id ? (
        <input
          type="checkbox"
          className="mt-1"
          aria-label={t('selectOrder', { order: o.order_number })}
          checked={!!selected}
          disabled={!onToggle}
          onChange={onToggle}
        />
      ) : null}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <Link to={`/nursery/orders/${o.id}`} className="font-medium text-primary-700 hover:underline">{o.order_number}</Link>
          <span className="text-neutral-800 break-words min-w-0">{o.customer_name}</span>
          {o.priority === 'asap' && (
            <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 text-orange-700 px-2 py-0.5 text-xs font-semibold">
              <Zap className="h-3 w-3" /> {t('asap')}
            </span>
          )}
          {o.due_class === 'overdue' && (
            <span className="rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-xs font-semibold">{t('overdueBadge')}</span>
          )}
          {o.due_class === 'undated' && (
            <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 text-neutral-600 px-2 py-0.5 text-xs font-medium">
              <CalendarX className="h-3 w-3" /> {t('noDate')}
            </span>
          )}
        </div>
        <div className="text-xs text-neutral-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
          <span>{t(`method.${o.despatch_method}`)}</span>
          {when && <span>{when}</span>}
          {o.despatch_method === 'cross_dock' ? (
            <span className="break-words min-w-0">
              {[o.customer_code ? t('store', { code: o.customer_code }) : null, o.customer_po ? t('po', { po: o.customer_po }) : null,
                o.xdock_del_seq ? t('delSeq', { seq: o.xdock_del_seq }) : null].filter(Boolean).join(' · ')}
            </span>
          ) : o.despatch_method !== 'collection' && addressLine(o) && <span className="break-words min-w-0">{addressLine(o)}</span>}
        </div>
        {o.load_id && (
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            <Link to={`/nursery/loads/${o.load_id}`} className="text-primary-700 hover:underline font-medium">{o.load_number}</Link>
            {o.load_despatch_date && <span className="text-neutral-500">{formatLocalDateInTz(o.load_despatch_date, tz)}</span>}
            {o.load_status && <LoadStatusBadge status={o.load_status} />}
          </div>
        )}
      </div>
      <div className={cn('text-right text-sm whitespace-nowrap', short ? 'text-amber-700' : 'text-neutral-700')}>
        <div className="font-semibold">{o.total_trees.toLocaleString()}</div>
        <div className="text-xs">{short ? t('allocatedN', { n: o.allocated_trees.toLocaleString() }) : t('allocatedFull')}</div>
      </div>
    </li>
  );
}

export default DespatchPlanner;
